import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@clerk/react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { makeApi } from "@/lib/api";
import { AddressInput, ADDRESS_HINT } from "@/components/AddressInput";
import { RateconAutofill, ReadingOverlay } from "@/components/ai/RateconAutofill";
import { RateconDropzone } from "@/components/ai/RateconDropzone";
import { BrokerPicker } from "@/components/brokers/BrokerPicker";
import { FieldLabel, AiBanner, AiConfirm, applyExtraction } from "@/components/ai/AiFill";
import { AppointmentInput } from "@/components/loads/AppointmentInput";
import { CarrierNumberField } from "@/components/loads/CarrierNumberField";

export default function CreateLoad() {
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const [drivers, setDrivers] = useState([]);
  const [form, setForm] = useState({
    carrier_number: "",
    load_number: "",
    broker_name: "",
    broker_id: "",
    rate: "",
    pickup_location: "",
    delivery_location: "",
    assigned_driver_id: "",
    pickup_date: "",
    delivery_date: "",
    pickup_appt: "",
    delivery_appt: "",
  });
  const [ratecon, setRatecon] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [reading, setReading] = useState(false);
  const [marks, setMarks] = useState(null);
  const [notes, setNotes] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [aiBroker, setAiBroker] = useState(null);
  const [aiMatchId, setAiMatchId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const api = makeApi(getToken);
        const { data } = await api.get("/drivers");
        setDrivers(data);
      } catch { /* ignore */ }
    })();
  }, [getToken]);

  const setField = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setMarks((m) => (m && m[k] ? { ...m, [k]: undefined } : m));
  };
  const update = (k) => (e) => setField(k, e.target?.value ?? e);

  const autofill = async (file) => {
    setRatecon(file);
    setReading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await makeApi(getToken).post("/ratecon/extract", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      const applied = applyExtraction(form, data);
      const m = data.broker_match;
      if (m) applied.form = { ...applied.form, broker_id: m.id, broker_name: m.name };
      else if (applied.marks.broker_name === "filled") applied.form = { ...applied.form, broker_id: "" };
      setAiBroker(data.broker || null);
      setAiMatchId(m?.id || null);
      setForm(applied.form);
      setMarks(applied.marks);
      setNotes(data.notes || "");
      setConfirmed(false);
      toast.success("Ratecon read. Please check every field.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't read this ratecon. It's still attached — please fill in the details by hand.");
    } finally {
      setReading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (marks && !confirmed) {
      toast.error("Please confirm you've checked the auto-filled details");
      return;
    }
    const blank = ["load_number", "broker_name", "pickup_location", "delivery_location"].some((k) => !String(form[k]).trim());
    if (blank || !(parseFloat(form.rate) > 0)) {
      toast.error(blank ? "Fill in all required fields" : "Rate must be more than $0");
      return;
    }
    setSubmitting(true);
    try {
      const api = makeApi(getToken);
      const { data: load } = await api.post("/loads", {
        ...form,
        carrier_number: form.carrier_number.trim() || null,
        rate: parseFloat(form.rate),
        assigned_driver_id: form.assigned_driver_id || null,
        broker_id: form.broker_id || null,
        broker_details: aiBroker && (!form.broker_id || form.broker_id === aiMatchId) ? aiBroker : null,
        pickup_date: form.pickup_date || null,
        delivery_date: form.delivery_date || null,
        pickup_appt: form.pickup_appt || null,
        delivery_appt: form.delivery_appt || null,
      });
      if (ratecon) {
        const fd = new FormData();
        fd.append("type", "Ratecon");
        fd.append("file", ratecon);
        await api.post(`/loads/${load.id}/documents`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      toast.success(`Load #${load.carrier_number} created`);
      navigate(`/admin/loads/${load.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create load");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="create-load-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "Outfit" }}>New load</h1>
        <p className="text-sm text-slate-500 mt-1">Upload the ratecon to auto-fill, or type the details yourself.</p>
      </div>

      <RateconAutofill onFile={autofill} busy={reading} fileName={marks ? ratecon?.name : null} />
      {marks && <AiBanner marks={marks} notes={notes} />}

      <Card className="relative p-6 md:p-8 border-slate-200">
        {reading && <ReadingOverlay />}
        <form onSubmit={submit} className="space-y-5">
          <div className="grid md:grid-cols-2 gap-5">
            <CarrierNumberField id="carrier_number" value={form.carrier_number} onChange={(v) => setField("carrier_number", v)} inputClass="" />
            <div>
              <FieldLabel htmlFor="load_number" mark={marks?.load_number} testId="load-number">Broker ref # *</FieldLabel>
              <Input id="load_number" value={form.load_number} onChange={update("load_number")} placeholder="e.g. 12345-A" data-testid="load-number-input" />
            </div>
            <div className="md:col-span-2">
              <FieldLabel htmlFor="broker_name" mark={marks?.broker_name} testId="broker">Broker / Customer *</FieldLabel>
              <BrokerPicker id="broker_name" brokerId={form.broker_id} name={form.broker_name} aiDetails={aiBroker}
                onChange={({ broker_id, broker_name }) => { setField("broker_name", broker_name); setForm((f) => ({ ...f, broker_id: broker_id || "" })); }} />
              {aiBroker?.address && form.broker_id && form.broker_id === aiMatchId && (
                <p className="mt-1 text-xs text-amber-700" data-testid="broker-address-update-hint">Saving will update this broker&apos;s address to the ratecon&apos;s: {aiBroker.address}</p>
              )}
            </div>
            <div>
              <FieldLabel htmlFor="rate" mark={marks?.rate} testId="rate">Rate (USD) *</FieldLabel>
              <Input id="rate" type="number" step="0.01" value={form.rate} onChange={update("rate")} placeholder="2500.00" data-testid="rate-input" />
            </div>
            <div>
              <Label>Assign driver</Label>
              <Select value={form.assigned_driver_id || "none"} onValueChange={(v) => setForm({ ...form, assigned_driver_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="driver-select"><SelectValue placeholder="Pick a driver" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Unassigned</SelectItem>
                  {drivers.map((d) => (
                    <SelectItem key={d.user_id} value={d.user_id}>{d.name || d.email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {drivers.length === 0 && (
                <p className="text-xs text-slate-500 mt-1">No drivers yet. Drivers appear here after they sign up.</p>
              )}
            </div>
            <div className="md:col-span-2">
              <FieldLabel htmlFor="pickup_location" mark={marks?.pickup_location} testId="pickup">Pickup *</FieldLabel>
              <AddressInput id="pickup_location" value={form.pickup_location} onChange={(v) => setField("pickup_location", v)} placeholder="City, ST or street address" testId="pickup-input" />
            </div>
            <div className="md:col-span-2">
              <FieldLabel htmlFor="delivery_location" mark={marks?.delivery_location} testId="delivery">Delivery *</FieldLabel>
              <AddressInput id="delivery_location" value={form.delivery_location} onChange={(v) => setField("delivery_location", v)} placeholder="City, ST or street address" testId="delivery-input" />
              <p className="mt-1.5 text-xs text-slate-500">{ADDRESS_HINT}</p>
            </div>
            <div>
              <FieldLabel htmlFor="pickup_date" mark={marks?.pickup_date} testId="pickup-date">Pickup date</FieldLabel>
              <Input id="pickup_date" type="date" value={form.pickup_date} onChange={update("pickup_date")} data-testid="pickup-date-input" />
            </div>
            <div>
              <FieldLabel htmlFor="delivery_date" mark={marks?.delivery_date} testId="delivery-date">Delivery date</FieldLabel>
              <Input id="delivery_date" type="date" value={form.delivery_date} onChange={update("delivery_date")} data-testid="delivery-date-input" />
            </div>
            <div>
              <FieldLabel htmlFor="pickup_appt" mark={marks?.pickup_appt} testId="pickup-appt">Pickup appointment</FieldLabel>
              <AppointmentInput value={form.pickup_appt} flagged={marks?.pickup_appt === "flagged"} onChange={(v) => setField("pickup_appt", v)} testId="pickup-appt-input" />
            </div>
            <div>
              <FieldLabel htmlFor="delivery_appt" mark={marks?.delivery_appt} testId="delivery-appt">Delivery appointment</FieldLabel>
              <AppointmentInput value={form.delivery_appt} flagged={marks?.delivery_appt === "flagged"} onChange={(v) => setField("delivery_appt", v)} testId="delivery-appt-input" />
            </div>
          </div>

          <div>
            <Label>Ratecon file {marks ? "(attached from auto-fill)" : "— optional"}</Label>
            <RateconDropzone file={ratecon} busy={reading} onFile={autofill} />
          </div>

          {marks && <AiConfirm checked={confirmed} onChange={setConfirmed} />}

          <div className="flex items-center justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => navigate("/admin")} data-testid="cancel-btn">Cancel</Button>
            <Button type="submit" disabled={submitting || reading || (marks && !confirmed)} className="bg-[#002FA7] hover:bg-[#001A75] rounded-full" data-testid="submit-load-btn">
              {submitting ? "Creating…" : "Create load"}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
